import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AdminService } from '../../../shared/services/admin.service';
import { LocaleService } from '../../../shared/services/locale.service';
import { StudentService } from '../../../shared/services/student.service';
import { empty } from '../../../functions';
import {
  ProfileLookingForUtils,
  ProfileTypeUtils,
} from '../../../shared/utils/student-profile.utils';
import { InterestService } from '../../../shared/services/interest.service';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  alertMessages,
  AlertService,
} from '../../../shared/services/alert.service';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { SelectorAreaofinterestsComponent } from 'src/app/core/selector-areaofinterests/selector-areaofinterests.component';
import { InputBirthdateComponent } from 'src/app/core/form-input-birthdate/input-birthdate.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommonModule } from '@angular/common';
import { StudentProfile } from 'src/app/api/models';
import { StudentProfileService } from 'src/app/api/services/student-profile.service';
import { TagService } from 'src/app/api/services/tag.service';
import { Tag } from 'src/app/api/models/tag';
import { TagInputComponent } from 'src/app/shared/components/tag-input/tag-input.component';

@Component({
  selector: 'app-modal-admin-studentprofile',
  templateUrl: './modal-admin-studentprofile.component.html',
  styleUrls: ['./modal-admin-studentprofile.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    FormsModule,
    SelectorAreaofinterestsComponent,
    InputBirthdateComponent,
    FontAwesomeModule,
    CommonModule,
    TagInputComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAdminStudentprofileComponent implements OnInit, OnDestroy {
  isProcessing = false;

  faSpinner = faSpinner;

  areaofinterestSubscription: Subscription = new Subscription();

  public data: any;

  public maxAmount = this.studentService.maxAmountAreaOfInterests;

  public entity: StudentProfile = {
    id: '',
    firstName: '',
    infix: '',
    lastName: '',
    birthDate: '',
    phoneNumber: '',
    profileType: <any>'',
    avatarUri: '',
    profileStrength: 0,
    homeAddress: {
      street: '',
      number: '',
      numberSuffix: '',
      city: '',
      postalCode: '',
      country: '',
    },
    allowEmailNotifications: false,
    hasParentalConsent: true,
    isSubscribedToNewsletter: false,
    hasMediaConsent: false,
    hasAcceptedGdprPolicy: false,
    hasDataUsageConsent: false,
    parentEmailAddress: '',
    interests: [],
    areaOfInterests: <any>[],
    workExperiences: <any>[],
    educations: <any>[],
    languageProficiencies: <any>[],
    foreignExperiences: <any>[],
    studentProfileCvs: <any>[],
    tags: [],
  };

  // Signal so the (async) loadData update triggers OnPush change detection and
  // the Vakgebieden selector renders the selection immediately (no interaction
  // needed). Bound via [ngModel]/(ngModelChange) in the template.
  public selectedAreaOfInterests = signal<any[]>([]);
  public areaOfInterests = signal<any[]>([]);

  lookingForData: any = [];
  profileTypes: any = [];
  tagSuggestions = signal<Tag[]>([]);

  /**
   * Constructor
   *
   * @param activeModal
   * @param adminService
   * @param locale
   */
  constructor(
    public activeModal: NgbActiveModal,
    public studentService: StudentService,
    public adminService: AdminService,
    public locale: LocaleService,
    public interestService: InterestService,
    public alert: AlertService,
    private studentProfileService: StudentProfileService,
    private tagService: TagService
  ) {}

  /**
   * Ng on init
   */
  ngOnInit(): void {
    this.lookingForData = ProfileLookingForUtils.getOptions();
    this.profileTypes = ProfileTypeUtils.getOptions();

    // Set area of interest data - always subscribe to the subject for updates
    this.areaofinterestSubscription =
      this.interestService.areaOfInterestsSubject.subscribe((result: any) => {
        if (result && result.length > 0) {
          this.areaOfInterests.set(result);
        }
      });

    // Check if data is already available
    if (!empty(this.interestService.areaOfInterests)) {
      this.areaOfInterests.set(this.interestService.areaOfInterests);
    } else {
      // Force load the data if not available
      this.interestService.loadInitialData();
    }

    this.entity = Object.assign({}, this.data.entity);

    if (this.entity['id']) {
      this.loadData(this.entity['id']);
    }

    // Load tag suggestions for the typeahead
    this.tagService
      .getTags()
      .pipe(catchError(() => of([])))
      .subscribe((result: any) => {
        const tags: Tag[] = result?.data ?? result?.items ?? result ?? [];
        this.tagSuggestions.set(tags);
      });
  }

  ngOnDestroy() {
    if (this.areaofinterestSubscription)
      this.areaofinterestSubscription.unsubscribe();
  }

  /**
   * Load all data of single item
   *
   * @param $id
   */
  loadData($id: string) {
    this.studentService
      .apiGetStudentprofile($id)
      .pipe(
        catchError(
          this.studentService.handleError('apiGetStudentprofile', false)
        )
      )
      .subscribe((data: any | boolean) => {
        if (data) {
          this.entity = Object.assign({}, data);
          this.selectedAreaOfInterests.set(
            Object.assign([], this.entity.areaOfInterests)
          );
        }
      });
  }

  /**
   * Save data
   *
   * @constructor
   */
  Save() {
    this.isProcessing = true;

    if (this.entity.id) {
      this.studentProfileService
        .updateStudentProfile({
          id: this.entity.id!,
          body: this.entity as StudentProfile,
        })
        .pipe(
          catchError(
            this.adminService.handleError('apiUpdateStudentprofile', false)
          )
        )
        .subscribe((result) => {
          if (result !== false && this.entity.id) {
            // Update area of interests
            this.studentService
              .apiUpdateAreaofinterests(
                this.entity.id,
                this.selectedAreaOfInterests()
              )
              .pipe(
                catchError(
                  this.adminService.handleError(
                    'apiUpdateAreaofinterests',
                    false
                  )
                )
              )
              .subscribe((result2) => {
                if (result2 !== false) {
                  this.isProcessing = false;
                  this.alert.success(alertMessages.successDataSaved);
                  this.activeModal.close(true);
                } else {
                  this.isProcessing = false;
                }
              });
          } else {
            this.isProcessing = false;
          }
        });
    }
  }
}
